'use client';
// Les conventions avec les cliniques, côté écran : types, couleurs, et la lecture des occurrences.
// Les DATES sont calculées par le moteur (`backend/src/conventions-calendrier.ts`) — l'écran ne
// recalcule jamais une règle : une seconde copie du calcul finirait par diverger de la première.
import React from 'react';

export type RegleConv =
  | { type: 'rang_mensuel'; jourSemaine: number; rang: number }
  | { type: 'intervalle'; depart: string; semaines: number }
  | { type: 'cycle'; depart: string; semainesActives: number; semainesArret: number }
  | { type: 'ponctuel'; date: string };

export interface ConventionVue {
  id: string; nom: string; contactCode?: string; nature?: 'convention' | 'evenement'; regle: RegleConv; description: string; actif: boolean;
  du: string | null; au: string | null; exceptions: string[]; couleur: string | null; note: string; ordre: number | null;
}

export interface OccurrenceConvention {
  date: string; conventionId: string; nom: string; couleur: string | null;
  nature: 'jour' | 'periode'; debutPeriode?: boolean; finPeriode?: boolean; semaine?: string;
  /** Référence de la fiche du contact (`ORG-0722`), pour ouvrir sa fiche d'un clic. */
  contactCode?: string;
  /** Rang d'affichage, posé par le moteur (`conventions-calendrier.ts`). */
  ordre?: number | null;
}

/* La règle des PISTES (une ligne stable par convention) vit dans une FEUILLE,
   `conventions-pistes.ts` : ce fichier-ci porte `'use client'` et un hook, donc
   le lanceur de tests ne sait pas le compiler. Réexportée ici pour que les
   écrans n'aient qu'un import à connaître. */
export { pistesConventions, casesDuJour, jourAvecConvention } from '@/lib/conventions-pistes';
export type { PisteConvention } from '@/lib/conventions-pistes';

export const COULEURS_CONVENTION = ['cyan', 'vert', 'ambre', 'rose', 'violet', 'gris'] as const;

const PALETTE: Record<string, { fond: string; texte: string; bord: string }> = {
  cyan: { fond: '#ECF8FD', texte: '#105576', bord: '#A6DFF6' },
  vert: { fond: '#E8F6EC', texte: '#1B6B3A', bord: '#A9DDB9' },
  ambre: { fond: '#FCF2E1', texte: '#7A4F00', bord: '#F0CF8B' },
  rose: { fond: '#FCE8EF', texte: '#8F1A47', bord: '#F2B5CB' },
  violet: { fond: '#EFE9FB', texte: '#53359A', bord: '#CBBDEE' },
  gris: { fond: '#EEF1F4', texte: '#43525F', bord: '#CBD5DE' },
};
export const couleurDe = (c?: string | null) => PALETTE[c || ''] || PALETTE.gris;

const EVENEMENT = 'bio24:conventions';
/** À appeler après toute écriture : les grilles et le calendrier se relisent. */
export function signalerConventions() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENEMENT));
}

/** Les occurrences entre deux dates (incluses), relues après chaque écriture. */
export function useOccurrences(du: string, au: string): { occurrences: OccurrenceConvention[]; charge: boolean } {
  const [rev, setRev] = React.useState(0);
  const [etat, setEtat] = React.useState<{ cle: string; occurrences: OccurrenceConvention[] } | null>(null);
  React.useEffect(() => {
    const relire = () => setRev((n) => n + 1);
    window.addEventListener(EVENEMENT, relire);
    return () => window.removeEventListener(EVENEMENT, relire);
  }, []);
  const cle = `${du}|${au}|${rev}`;
  React.useEffect(() => {
    if (!du || !au) return undefined;
    let vivant = true;
    void fetch(`/api/ged/ged/rh/conventions/occurrences?du=${du}&au=${au}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { occurrences: [] }))
      .catch(() => ({ occurrences: [] }))
      .then((d: { occurrences?: OccurrenceConvention[] }) => { if (vivant) setEtat({ cle, occurrences: d.occurrences || [] }); });
    return () => { vivant = false; };
  }, [du, au, cle]);
  return { occurrences: etat?.occurrences || [], charge: etat?.cle !== cle };
}

export function parDate(occ: OccurrenceConvention[]): Map<string, OccurrenceConvention[]> {
  const m = new Map<string, OccurrenceConvention[]>();
  for (const o of occ) (m.get(o.date) || m.set(o.date, []).get(o.date)!).push(o);
  return m;
}

/** Regroupe par convention : « Carthage, semaine 1/2 » une fois, pas sept fois. */
export function parConvention(occ: OccurrenceConvention[]): { conventionId: string; nom: string; couleur: string | null; jours: string[]; semaine?: string }[] {
  const m = new Map<string, { conventionId: string; nom: string; couleur: string | null; jours: string[]; semaine?: string }>();
  for (const o of occ) {
    const g = m.get(o.conventionId) || { conventionId: o.conventionId, nom: o.nom, couleur: o.couleur, jours: [] as string[], semaine: o.semaine };
    g.jours.push(o.date);
    m.set(o.conventionId, g);
  }
  return [...m.values()];
}
